"use client";
/* oxlint-disable react/exhaustive-effect-dependencies, react/set-state-in-effect -- reviewed: legacy patterns, behavior preserved */
import { LoadingText } from "@/components/LoadingText";
import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { StudioSourceLink } from "@/components/studio/StudioSourceLink";
import {
  MangaReader as BaseMangaReader,
  type MangaReaderProps as ReaderProps,
} from "@aphrody/shenron-ui/reader";
import { mangaReaderAssetUrl } from "@/lib/manga-reader-assets";
import { useKoharuCanvasWarmup } from "@aphrody/shenron-ui/reader";
import { MangaPreviewNotice, type MangaReaderExtent } from "./MangaPreviewNotice";

export type MangaReaderProps = Pick<
  ReaderProps,
  "pages" | "title" | "direction" | "prevHref" | "nextHref" | "prevPages" | "nextPages"
> & {
  chapterId?: number;
  sourceKind?: "cdn" | "editorial";
  readerExtent?: MangaReaderExtent;
};

const renderChapterLink: NonNullable<ReaderProps["renderChapterLink"]> = ({
  href,
  children,
  className,
  ariaLabel,
}) => (
  <Link href={href} className={className} aria-label={ariaLabel} title={ariaLabel}>
    {children}
  </Link>
);

export function MangaReader({ chapterId, sourceKind, readerExtent, ...props }: MangaReaderProps) {
  const canvasWarmup = useKoharuCanvasWarmup();
  const [initialPage, setInitialPage] = useState(1);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    // `?page=` ; l'ancien `?planche=` (liens déjà partagés) reste lu.
    const query = new URLSearchParams(window.location.search);
    const requested = Number(query.get("page") ?? query.get("planche"));
    if (Number.isFinite(requested) && requested >= 1) {
      setInitialPage(Math.trunc(requested));
    } else {
      setInitialPage(1);
    }
    setReady(true);
  }, [chapterId]);
  useEffect(() => {
    if (!chapterId || readerExtent?.kind === "preview") return;
    try {
      const read = JSON.parse(localStorage.getItem("dbfr_read_chapters") ?? "[]");
      const ids: number[] = Array.isArray(read) ? read.map(Number) : [];
      if (!ids.includes(chapterId)) ids.push(chapterId);
      // Progression chapitre par chapitre (succès, coches du sommaire). Le « reprendre la
      // lecture » passe par l’historique transverse (`TrackHistory` → `ContinueRail`).
      localStorage.setItem("dbfr_read_chapters", JSON.stringify(ids));
    } catch (error) {
      console.error("Failed to save read chapter", error);
    }
  }, [chapterId, readerExtent?.kind]);
  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => setCurrentPage(initialPage), [initialPage, chapterId]);
  const onPageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      if (!chapterId || !ready) return;
      const url = new URL(window.location.href);
      if (url.searchParams.get("page") === String(page) && !url.searchParams.has("planche")) return;
      url.searchParams.delete("planche");
      url.searchParams.set("page", String(page));
      window.history.replaceState(null, "", url.toString());
    },
    [chapterId, ready],
  );
  const apiPageHref = useCallback(
    (page: number) => (chapterId ? `/api/manga/chapter/${chapterId}/pages/${page}` : null),
    [chapterId],
  );
  const resolveAssetUrl = useCallback(
    (url: string) => mangaReaderAssetUrl(url, sourceKind),
    [sourceKind],
  );
  return (
    <div className="m3-reader--dragon-ball" data-koharu-canvas={canvasWarmup}>
      <MangaPreviewNotice extent={readerExtent} count={props.pages.length} />
      {chapterId && <StudioSourceLink kind="chapter" id={chapterId} page={currentPage} />}
      <BaseMangaReader
        key={`${chapterId ?? "ch"}-${initialPage}`}
        {...props}
        initialPage={initialPage}
        onPageChange={onPageChange}
        apiPageHref={apiPageHref}
        resolveAssetUrl={resolveAssetUrl}
        renderChapterLink={renderChapterLink}
      />
      {chapterId ? <TraductionFr chapterId={chapterId} page={currentPage} /> : null}
    </div>
  );
}

type TexteTraduit = {
  id: number | string;
  readingOrder: number;
  sourceText: string;
  translatedText: string;
};

/** Bascule « Traduction FR » : bulles traduites publiées de la planche courante. */
function TraductionFr({ chapterId, page }: { chapterId: number; page: number }) {
  const [active, setActive] = useState(false);
  const [state, setState] = useState<{ key: string; texts: TexteTraduit[] | null } | null>(null);
  const key = `${chapterId}/${page}`;
  useEffect(() => {
    if (!active || state?.key === key) return;
    const ctrl = new AbortController();
    fetch(`/api/manga/chapter/${chapterId}/pages/${page}/translation`, { signal: ctrl.signal })
      .then(async (res) =>
        res.ok ? (((await res.json()) as { texts?: TexteTraduit[] }).texts ?? []) : [],
      )
      .then((texts) => setState({ key, texts }))
      .catch((error: unknown) => {
        if ((error as { name?: string })?.name !== "AbortError") setState({ key, texts: [] });
      });
    return () => ctrl.abort();
  }, [active, chapterId, page, key, state?.key]);
  const texts = state?.key === key ? state.texts : null;
  return (
    <section className="m3-reader__translation" aria-label="Traduction française">
      <button
        type="button"
        aria-pressed={active}
        onClick={() => setActive((v) => !v)}
        className="m3-reader__translation-toggle"
      >
        Traduction FR
      </button>
      {active ? (
        texts === null ? (
          <p className="m3-reader__translation-status">
            <LoadingText />
          </p>
        ) : texts.length === 0 ? (
          <p className="m3-reader__translation-status">
            Aucune traduction publiée pour cette planche.
          </p>
        ) : (
          <ol className="m3-reader__translation-list">
            {texts.map((t) => (
              <li key={String(t.id)} lang="fr">
                {t.translatedText}
              </li>
            ))}
          </ol>
        )
      ) : null}
    </section>
  );
}
